/**
 * RulePairNote — plain-words explainer under a rule group's header telling the
 * user what the chosen ACTION COMBINATION will actually do on rotation:
 * a rotate verb + "Add new element" is a SWAP (nothing is removed while the
 * queue has no ready replacement); a rotate verb alone removes without adding.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowLeftRight, CircleDashed, MinusCircle, Play } from 'lucide-react';
import type { FlowLayer, RuleAction } from '@trafficpulse/shared';

type RotationVerb = 'set-to-queue' | 'set-to-waiting' | 'eliminate' | 'pause';

const VERB_LABEL: Record<RotationVerb, string> = {
  'set-to-queue': 'sets an element back to the queue',
  'set-to-waiting': 'parks an element in waiting',
  eliminate: 'eliminates an element',
  pause: 'takes capped offers off a path (the whole path when none would stay)',
};

/** Most final first — mirrors the engine's precedence. */
const VERB_ORDER: readonly RotationVerb[] = [
  'eliminate',
  'set-to-waiting',
  'set-to-queue',
  'pause',
];

const NEW_ELEMENT: Record<FlowLayer, string> = {
  paths: 'new path',
  landers: 'new lander',
  offers: 'new offer',
};

export interface RulePairNoteProps {
  actions: RuleAction[];
  appliesTo: ReadonlyArray<FlowLayer>;
}

const RulePairNote: FC<RulePairNoteProps> = ({ actions, appliesTo }) => {
  // A brand-new group carries no action yet. Say so plainly — otherwise the
  // group looks finished and quietly never fires.
  if (actions.length === 0) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2 py-1 text-[12px] leading-snug text-muted-foreground">
        <CircleDashed
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning"
          aria-hidden
        />
        <span>
          <span className="font-semibold text-foreground">No action yet:</span>{' '}
          this rule can match but will do nothing. Pick an action to make it
          count.
        </span>
      </p>
    );
  }
  if (actions.includes('reactivate')) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-success/30 bg-success/5 px-2 py-1 text-[12px] leading-snug text-muted-foreground">
        <Play className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
        <span>
          <span className="font-semibold text-foreground">Brings back:</span>{' '}
          a paused offer returns to its path and a cap-removed path returns to
          the queue once their partner cap has room again — only the offers that
          also pass this rule&apos;s other conditions (the preset asks for the
          path&apos;s BEST performance tag). Runs on the CAP
          tracking schedule. Nothing is removed.
        </span>
      </p>
    );
  }
  const verb = VERB_ORDER.find((v) => actions.includes(v));
  if (!verb) return null;
  // A cap pause is never held for a replacement: a fully capped path must go
  // even into a hole. Add new element only fills the slot when one is ready.
  if (verb === 'pause') {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning/5 px-2 py-1 text-[12px] leading-snug text-muted-foreground">
        <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
        <span>
          <span className="font-semibold text-foreground">Partner caps:</span>{' '}
          offers at their cap leave the path; when none would stay the whole path
          is removed — even if the queue has nothing to replace it.{' '}
          {actions.includes('add-element')
            ? 'Add new element fills the freed slot as soon as a queued path is ready.'
            : 'Tick Add new element to fill the freed slot from the queue.'}
        </span>
      </p>
    );
  }
  const element = appliesTo.length
    ? appliesTo.map((l) => NEW_ELEMENT[l]).join(' / ')
    : 'new element';
  if (actions.includes('add-element')) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-[12px] leading-snug text-muted-foreground">
        <ArrowLeftRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
        <span>
          <span className="font-semibold text-foreground">Works as a swap:</span>{' '}
          this rule only {VERB_LABEL[verb]} when the queue has a {element} ready
          to take its place. Empty queue → nothing is removed (the element stays
          live, shown as “waiting for a replacement”).
        </span>
      </p>
    );
  }
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-[12px] leading-snug text-muted-foreground">
      <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        Removes without adding a replacement — tick{' '}
        <span className="font-medium text-foreground">Add new element</span> to
        make it a swap that waits for a queued {element}.
      </span>
    </p>
  );
};

export default RulePairNote;
